import { Button, Menu, MenuItem } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  isPremiumLayer,
  layerLabels,
  layers,
  nullLayerLabel,
  resolveAccessibleLayer,
} from '../enums';
import { useLayer } from '../LayerContext/useLayer';

const neutralLayerButtonStyles = {
  px: 2,
  color: 'text.primary',
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'outline',
  '&:hover': {
    bgcolor: 'primary.main',
    borderColor: 'primary.main',
    color: 'primary.contrastText',
  },
};
const controlsContainerStyles = {
  position: 'absolute' as const,
  top: 0,
  left: 0,
  zIndex: 10,
  padding: '16px',
};

interface LayerMenuItemProps {
  layerKey: string | null;
  label: string;
  onSelect: (layerKey: string | null) => void;
}

const LayerMenuItem = React.memo(
  ({ layerKey, label, onSelect }: LayerMenuItemProps) => {
    const handleClick = () => onSelect(layerKey);

    return <MenuItem onClick={handleClick}>{label}</MenuItem>;
  }
);

const MapControls = ({
  allowedLayers,
  premiumLayersAvailable,
  showNullLayer: showNullLayerProp,
  neutralStyle = false,
}: {
  allowedLayers?: string[];
  premiumLayersAvailable?: boolean;
  showNullLayer?: boolean;
  neutralStyle?: boolean;
}) => {
  const { layer, setLayer } = useLayer();
  const anchorMRef = useRef<HTMLButtonElement>(null);

  const [mOpen, setMOpen] = useState(false);

  const handleMenuToggle = useCallback(() => {
    setMOpen((open) => !open);
  }, []);
  const handleMenuClose = useCallback(() => setMOpen(false), []);
  const handleLayerSelect = useCallback(
    (nextLayer: string | null) => {
      setLayer(nextLayer);
      setMOpen(false);
    },
    [setLayer]
  );

  const layerKeys = (allowedLayers ?? Object.keys(layers)).filter(
    (layerKey) => !isPremiumLayer(layerKey) || premiumLayersAvailable === true
  );
  const showNullLayer = showNullLayerProp ?? !allowedLayers;

  useEffect(() => {
    if (premiumLayersAvailable !== false) {
      return;
    }

    const accessibleLayer = resolveAccessibleLayer(layer, false);

    if (accessibleLayer !== layer) {
      setLayer(accessibleLayer);
    }
  }, [layer, premiumLayersAvailable, setLayer]);

  return (
    <div style={controlsContainerStyles}>
      <Button
        size={neutralStyle ? 'small' : 'medium'}
        variant={neutralStyle ? 'text' : 'contained'}
        onClick={handleMenuToggle}
        ref={anchorMRef}
        sx={neutralStyle ? neutralLayerButtonStyles : undefined}
      >
        {layerLabels[layer] || nullLayerLabel}
      </Button>
      <Menu
        anchorEl={anchorMRef.current}
        id="buttons-menu"
        onClose={handleMenuClose}
        open={mOpen}
      >
        {layerKeys.map((layerKey) => (
          <LayerMenuItem
            key={`option-layer-${layerKey}`}
            layerKey={layerKey}
            label={layerLabels[layerKey]}
            onSelect={handleLayerSelect}
          />
        ))}
        {showNullLayer && (
          <LayerMenuItem
            layerKey={null}
            label={nullLayerLabel}
            onSelect={handleLayerSelect}
          />
        )}
      </Menu>
    </div>
  );
};

export { MapControls };
